import React, { useEffect, useState } from "react";
import { TrendingUp, Scale, Siren } from "lucide-react";
import { fetchAnalytics, fetchBenchmarks, fetchAnomalies } from "../../api";

const fmtMoney = (n) =>
  `$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

const TrendChart = ({ trend }) => {
  if (!trend.length) return null;
  const W = 500;
  const H = 150;
  const max = Math.max(...trend.flatMap((t) => [t.inflow, t.outflow]), 1);
  const x = (i) => (trend.length === 1 ? W / 2 : (i / (trend.length - 1)) * W);
  const y = (v) => H - (v / max) * (H - 20);
  const line = (key) => trend.map((t, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(t[key])}`).join(" ");
  const area = `${line("inflow")} L${x(trend.length - 1)},${H} L0,${H} Z`;
  return (
    <>
      <div className="h-56 w-full flex items-end pt-6">
        <svg className="w-full h-full overflow-visible" viewBox={`0 0 ${W} ${H}`} data-testid="trend-chart-svg">
          <defs>
            <linearGradient id="chartGlow" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={area} fill="url(#chartGlow)" />
          <path d={line("inflow")} fill="none" stroke="#3B82F6" strokeWidth="3" />
          <path d={line("outflow")} fill="none" stroke="#f59e0b" strokeWidth="2" strokeDasharray="6 4" />
          {trend.map((t, i) => (
            <circle key={t.month} cx={x(i)} cy={y(t.inflow)} r="4" className="fill-blue-500 stroke-[#0A0A0A] stroke-2" />
          ))}
        </svg>
      </div>
      <div className="flex justify-between text-[11px] text-zinc-400 font-mono pt-2 border-t border-white/10">
        {trend.map((t) => (
          <span key={t.month}>{t.label}</span>
        ))}
      </div>
      <div className="flex items-center space-x-4 text-[11px] text-zinc-400">
        <span className="flex items-center"><span className="w-3 h-0.5 bg-blue-500 mr-1.5 inline-block"></span>Inflows</span>
        <span className="flex items-center"><span className="w-3 h-0.5 bg-amber-500 mr-1.5 inline-block"></span>Outflows</span>
      </div>
    </>
  );
};

const AnalyticsView = () => {
  const [data, setData] = useState(null);
  const [bench, setBench] = useState(null);
  const [anomalies, setAnomalies] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchAnalytics().then(setData).catch(() => setError(true));
    fetchBenchmarks().then(setBench).catch(() => {});
    fetchAnomalies().then(setAnomalies).catch(() => {});
  }, []);

  if (error)
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-rose-400" data-testid="view-analytics">
        Failed to load analytics from the engine.
      </div>
    );
  if (!data)
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-zinc-400 animate-pulse" data-testid="view-analytics">
        Computing P&amp;L and analytics…
      </div>
    );

  const { kpis, pl, metrics, allocation, trend, vendors } = data;

  return (
    <div className="flex-1 overflow-y-auto custom-scrollbar p-8" data-testid="view-analytics">
      <div className="max-w-6xl mx-auto space-y-8">
        <div className="border-b border-white/10 pb-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-white">Executive KPI &amp; Tax Analytics</h1>
            <p className="text-zinc-400 text-xs mt-1">
              Reconciled to the trial balance — release blocked unless every figure ties out.
            </p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${pl.foots ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400" : "bg-rose-500/10 border-rose-500/30 text-rose-400"}`} data-testid="pl-foots-badge">
            {pl.foots ? "P&L Foots \u2713" : "P&L Does Not Foot"}
          </span>
        </div>

        {/* KPI METRIC CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-cards">
          {kpis.map((card) => (
            <div key={card.label} className="glass-card p-5 rounded-md border border-white/10 space-y-2 transition duration-300 hover:border-white/20 hover:border-white/20">
              <div className="text-xs text-zinc-400 font-medium">{card.label}</div>
              <div className={`text-2xl font-black ${card.valueClass}`}>{card.value}</div>
              <div className={`text-[11px] flex items-center ${card.subClass}`}>
                {card.trend && <TrendingUp className="w-3 h-3 mr-1" />}
                {card.sub}
              </div>
            </div>
          ))}
        </div>

        {/* CHARTS ROW */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 glass-card p-6 rounded-md border border-white/10 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-white">Monthly Cash Activity (Post-Elimination)</h3>
              <span className="text-xs text-zinc-400 font-mono">Tax Year 2025</span>
            </div>
            <TrendChart trend={trend} />
          </div>

          <div className="glass-card p-6 rounded-md border border-white/10 space-y-4">
            <h3 className="text-sm font-bold text-white">Outflow Allocation</h3>
            <div className="space-y-3 text-xs" data-testid="allocation-bars">
              {allocation.map((item) => (
                <div key={item.label}>
                  <div className="flex justify-between text-zinc-400 mb-1">
                    <span>{item.label}</span>
                    <span className="font-bold text-zinc-200">{item.pct}%</span>
                  </div>
                  <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                    <div className={`${item.barClass} h-full transition-all duration-700`} style={{ width: `${item.pct}%` }}></div>
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">{fmtMoney(item.amount)}</div>
                </div>
              ))}
            </div>

            <div className="pt-3 border-t border-white/10 space-y-1.5 text-[11px]" data-testid="pl-summary">
              <div className="flex justify-between"><span className="text-zinc-400">Net Revenue</span><span className="font-bold text-zinc-200">{fmtMoney(pl.total_revenue)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">COGS + OpEx</span><span className="font-bold text-zinc-200">{fmtMoney(pl.total_cogs + pl.total_opex)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Net Income</span><span className={`font-bold ${pl.net_income < 0 ? "text-rose-400" : "text-emerald-400"}`}>{pl.net_income < 0 ? "-" : ""}{fmtMoney(pl.net_income)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Net Margin</span><span className="font-bold text-zinc-200">{pl.net_margin_pct}%</span></div>
            </div>
          </div>
        </div>

        {/* METRICS + VENDORS */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="glass-card p-6 rounded-md border border-white/10 space-y-3">
            <h3 className="text-sm font-bold text-white">Performance Metrics</h3>
            <div className="space-y-2 text-[11px]" data-testid="performance-metrics">
              {[
                ["Break-even revenue", fmtMoney(metrics.break_even_revenue)],
                ["Margin of safety", `${metrics.margin_of_safety_pct}%`],
                ["Fixed cost ratio", `${metrics.fixed_cost_ratio}%`],
                ["Payroll ratio", `${metrics.payroll_ratio}%`],
                ["Occupancy ratio", `${metrics.occupancy_ratio}%`],
                ["Top-5 vendor concentration", `${metrics.vendor_concentration_top5}%`],
                ["Top-5 customer concentration", `${metrics.customer_concentration_top5}%`],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between py-1 border-b border-white/5 last:border-0">
                  <span className="text-zinc-400">{k}</span>
                  <span className="font-bold font-mono text-zinc-200">{v}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2 glass-card p-6 rounded-md border border-white/10 space-y-3">
            <h3 className="text-sm font-bold text-white">Where Did The Money Go — Top Vendors</h3>
            <table className="w-full text-left text-[11px]" data-testid="vendor-table">
              <thead className="text-zinc-400 uppercase tracking-wider border-b border-white/10">
                <tr>
                  <th className="py-2">Vendor</th>
                  <th className="py-2">Nature</th>
                  <th className="py-2 text-right">Total</th>
                  <th className="py-2 text-center">Txns</th>
                  <th className="py-2 text-right">Monthly Run Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 text-zinc-300">
                {vendors.map((v) => (
                  <tr key={v.vendor}>
                    <td className="py-2 font-semibold">{v.vendor}</td>
                    <td className="py-2 text-zinc-400">{v.nature}</td>
                    <td className="py-2 text-right font-mono font-semibold">{fmtMoney(v.total)}</td>
                    <td className="py-2 text-center">{v.txn_count}</td>
                    <td className="py-2 text-right font-mono">{v.recurring ? `${fmtMoney(v.monthly_run_rate)}/mo` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ANOMALY ALERTS */}
        {anomalies && anomalies.alerts.length > 0 && (
          <div className="glass-card p-6 rounded-md border border-white/10 space-y-4" data-testid="anomalies-card">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <h3 className="text-sm font-bold text-white flex items-center">
                <Siren className="w-4 h-4 mr-2 text-rose-400" /> Anomaly Alerts
                <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 text-[10px] font-black" data-testid="anomaly-count">
                  {anomalies.warning_count} warning{anomalies.warning_count === 1 ? "" : "s"} · {anomalies.count} total
                </span>
              </h3>
              <span className="text-xs text-zinc-400">Surfaced before review — spikes, rate drift &amp; new vendors</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="anomaly-list">
              {anomalies.alerts.map((a, i) => (
                <div
                  key={`${a.type}-${a.vendor}-${i}`}
                  data-testid={`anomaly-${a.type}-${i}`}
                  className={`rounded-md border p-3.5 text-xs space-y-1.5 ${
                    a.severity === "warning" ? "bg-amber-500/10 border-amber-500/30" : "bg-white/[0.04] border-white/10"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-white">{a.vendor}</span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase ${
                        a.type === "spend_spike"
                          ? "bg-rose-500/10 text-rose-400"
                          : a.type === "rate_drift"
                          ? "bg-amber-500/10 text-amber-400"
                          : a.type === "new_vendor"
                          ? "bg-blue-500/10 text-blue-300"
                          : "bg-white/10 text-zinc-400"
                      }`}
                    >
                      {a.type.replace(/_/g, " ")}
                    </span>
                  </div>
                  <p className="text-zinc-400 leading-relaxed">{a.detail}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* BENCHMARKS */}
        {bench && bench.benchmarks.length > 0 && (
          <div className="glass-card p-6 rounded-md border border-white/10 space-y-4" data-testid="benchmarks-card">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <h3 className="text-sm font-bold text-white flex items-center">
                <Scale className="w-4 h-4 mr-2 text-blue-400" /> Industry Benchmark Comparison
              </h3>
              <span className="text-xs text-zinc-400 font-mono">NAICS {bench.naics} · {bench.benchmarks[0].source}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {bench.benchmarks.map((b) => {
                const scaleMax = Math.max(b.entity_value, b.cohort_median) * 1.3;
                return (
                  <div key={b.ratio} className="space-y-2" data-testid={`benchmark-${b.ratio}`}>
                    <div className="flex justify-between text-xs">
                      <span className="font-bold text-white">{b.label}</span>
                      <span className="text-zinc-400">{b.position} (cohort n={b.cohort_size})</span>
                    </div>
                    <div className="relative w-full h-3 bg-white/5 rounded-full overflow-hidden">
                      <div className="absolute h-full bg-blue-500/80 rounded-full" style={{ width: `${(b.entity_value / scaleMax) * 100}%` }}></div>
                      <div className="absolute h-full w-0.5 bg-white/70" style={{ left: `${(b.cohort_median / scaleMax) * 100}%` }} title={`Cohort median ${b.cohort_median}%`}></div>
                    </div>
                    <div className="flex justify-between text-[11px]">
                      <span className="text-blue-300 font-bold font-mono">Entity {b.entity_value}%</span>
                      <span className="text-zinc-400 font-mono">Median {b.cohort_median}%</span>
                    </div>
                    <div className={`text-[11px] rounded-md px-2.5 py-1.5 border ${b.gap_pp > 0 ? "bg-amber-500/10 border-amber-500/30 text-amber-800" : "bg-emerald-500/10 border-emerald-500/30 text-emerald-800"}`}>
                      Gap: {b.gap_pp > 0 ? "+" : ""}{b.gap_pp} pts vs median ≈{" "}
                      <strong className="font-mono">{fmtMoney(b.dollar_impact)}</strong> {b.gap_pp > 0 ? "above" : "below"} cohort spend at your revenue
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-zinc-500 leading-relaxed border-t border-white/5 pt-2">{bench.limitations}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsView;
